import Decimal from "decimal.js";
import { formatUserName, toLocalDateOnlyString } from "@forgepoint/shared";
import type {
  Address,
  DateOnlyString,
  PublicQuote,
  PublicQuoteDiscountLine,
  PublicQuoteItemLine,
  PublicQuoteLine,
  PublicQuoteQuantityBreak,
  WeightUnit,
} from "@forgepoint/shared";

export { computeLineNumbers } from "@forgepoint/shared";

export const getDiscountLines = (
  lines: PublicQuoteLine[],
): PublicQuoteDiscountLine[] => {
  return lines.filter(
    (line): line is PublicQuoteDiscountLine => line.type === "DISCOUNT",
  );
};

/**
 * Returns an item line's editable description, falling back to the catalog description.
 * @param line - The item line to describe
 * @returns The description, or null when empty or a repeat of the line heading
 */
export const getItemLineDescription = (
  line: PublicQuoteItemLine,
): string | null => {
  const heading = line.item?.name || line.description;
  const description = line.description || line.item?.description;

  if (!description || description === heading) {
    return null;
  }

  return description;
};

/**
 * Groups total weights by weight unit across item lines.
 * Returns empty if any item line has multiple quantity breaks (same as cost totals).
 * @param lines - The quote lines to aggregate weights from
 * @returns An array of [weightUnit, totalWeight] tuples
 */
export const getTotalWeightsByUnit = (
  lines: PublicQuoteLine[],
): [WeightUnit, Decimal][] => {
  const itemLines = lines.filter(
    (l): l is PublicQuoteItemLine => l.type === "ITEM",
  );
  if (itemLines.some((l) => l.quantityBreaks.length > 1)) {
    return [];
  }
  const grouped: Partial<Record<WeightUnit, Decimal>> = {};
  for (const line of itemLines) {
    const firstBreak = line.quantityBreaks[0];
    const breakWeight = firstBreak?.totalWeight;
    const unit = getBreakWeightUnit(firstBreak, line);
    if (!breakWeight || !unit) {
      continue;
    }
    grouped[unit] = (grouped[unit] ?? new Decimal(0)).add(
      new Decimal(breakWeight),
    );
  }
  return Object.entries(grouped) as [WeightUnit, Decimal][];
};

/**
 * Picks the unit a quantity break's total weight is in.
 * @param quantityBreak - The quantity break carrying the total weight
 * @param line - The item line the break belongs to
 * @returns The break's weight unit, else the item's, or null when neither is set
 */
export const getBreakWeightUnit = (
  quantityBreak: PublicQuoteQuantityBreak | undefined,
  line: PublicQuoteItemLine,
): WeightUnit | null =>
  quantityBreak?.totalWeightUnit ?? line.item?.weightUnit ?? null;

/**
 * Builds an array of non-empty address display lines from an Address object.
 * Returns null if the address is nullish or produces no lines.
 * @param address - The address to format
 * @returns An array of display-ready strings, or null
 */
export const formatAddressLines = (
  address?: Address | null,
): string[] | null => {
  if (!address) {
    return null;
  }

  const cityState = [address.city, address.state].filter(Boolean).join(", ");
  const cityLine =
    [cityState || null, address.zipcode || null]
      .filter((value): value is string => Boolean(value))
      .join(" ") || null;

  const lines = [
    address.address_line_1,
    address.address_line_2,
    cityLine,
    address.country,
  ].filter((value): value is string => Boolean(value));

  if (lines.length === 0) {
    return null;
  }

  return lines;
};

export const getPublicQuoteBrandName = (
  quote: Pick<PublicQuote, "location" | "organization">,
): string | null => {
  const locationName = quote.location?.name?.trim() || null;
  if (locationName) {
    const displayName = removeTrailingParenthetical(locationName);
    if (isGmrQuote(quote)) {
      return `${displayName} (a GMR company)`;
    }
    return locationName;
  }

  return quote.organization?.name?.trim() || null;
};

/** The quote's document date as `YYYY-MM-DD`: the quote date, or the creation day when it is not set. */
export const getQuoteEffectiveDate = (
  quote: Pick<PublicQuote, "quoteDate" | "createdAt">,
): DateOnlyString => quote.quoteDate ?? toLocalDateOnlyString(quote.createdAt);

/** The quote's people as full names, comma separated; a nameless person shows their email. */
export const publicQuotePeopleNames = (quote: PublicQuote): string =>
  quote.people
    .map((person) =>
      formatUserName(person.firstName, person.lastName, person.email ?? ""),
    )
    .filter((name) => name !== "")
    .join(", ");

/** The quote's people's emails, comma separated, skipping people without one. */
export const publicQuotePeopleEmails = (quote: PublicQuote): string =>
  quote.people
    .map((person) => person.email ?? "")
    .filter((email) => email !== "")
    .join(", ");

const isGmrQuote = (quote: Pick<PublicQuote, "organization">): boolean => {
  return (quote.organization?.name ?? "").toLowerCase().includes("gmr");
};

const removeTrailingParenthetical = (value: string): string => {
  return value.replace(/\s*\([^)]*\)\s*$/, "").trim() || value;
};
